/**
 * <s-size-picker>: optional Waistband and Inseam square size buttons.
 * Nothing is required; buttons rendered with aria-pressed="true" start selected. The best-matching variant ID
 * goes into the form's hidden `id` input (the first variant when nothing is picked).
 * While a size is missing, a "Sizes" line property tells the shop to confirm them.
 */
class SSizePicker extends HTMLElement {
  /** @type {AbortController | undefined} */
  #ac;
  /** @type {Record<string, string>} */
  #picked = {};
  /** @type {Array<{ id: number, options: string[] }>} */
  #variants = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    try {
      this.#variants = JSON.parse(this.querySelector('[data-size-variants]')?.textContent || '[]');
    } catch {
      this.#variants = [];
    }

    this.querySelectorAll('[data-size-option]').forEach((button) => {
      button.addEventListener('click', () => this.#pick(/** @type {HTMLButtonElement} */ (button)), opts);
      // Sizes rendered as already pressed (a page's default picks) start selected.
      if (button.getAttribute('aria-pressed') === 'true') {
        const index = button.closest('[data-size-group]')?.getAttribute('data-size-group') || '0';
        this.#picked[index] = /** @type {HTMLButtonElement} */ (button).value;
      }
    });

    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #form() {
    return this.closest('form');
  }

  /** @param {HTMLButtonElement} button */
  #pick(button) {
    const group = button.closest('[data-size-group]');
    if (!group) return;
    const index = group.getAttribute('data-size-group') || '0';
    // Tapping the picked size again clears it, since sizes are optional.
    const clearing = this.#picked[index] === button.value;
    group.querySelectorAll('[data-size-option]').forEach((option) => {
      option.setAttribute('aria-pressed', !clearing && option === button ? 'true' : 'false');
    });
    const valueEl = group.querySelector('[data-size-value]');
    if (clearing) {
      delete this.#picked[index];
      if (valueEl) {
        valueEl.textContent = this.dataset.notSelected || '';
        valueEl.classList.add('text-on-surface-secondary');
      }
    } else {
      this.#picked[index] = button.value;
      if (valueEl) {
        valueEl.textContent = button.value;
        valueEl.classList.remove('text-on-surface-secondary');
      }
    }
    this.#update();
  }

  #complete() {
    const groups = Array.from(this.querySelectorAll('[data-size-group]'));
    return groups.every((group) => this.#picked[group.getAttribute('data-size-group') || '0']);
  }

  #variant() {
    const picks = Object.entries(this.#picked);
    return (
      this.#variants.find((variant) => picks.every(([index, value]) => variant.options[Number(index)] === value)) ||
      this.#variants[0] ||
      null
    );
  }

  #update() {
    const form = this.#form();
    if (!form) return;
    const variant = this.#variant();
    const idInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]'));
    if (variant && idInput) idInput.value = String(variant.id);

    let note = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-size-note]'));
    if (!note) {
      note = document.createElement('input');
      note.type = 'hidden';
      note.name = `properties[${this.dataset.noteLabel || 'Sizes'}]`;
      note.value = this.dataset.noteValue || 'To be confirmed';
      note.setAttribute('data-size-note', '');
      form.append(note);
    }
    note.disabled = this.#complete();
  }
}

if (!customElements.get('s-size-picker')) customElements.define('s-size-picker', SSizePicker);
